import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService, UnitService } from '../services/api';
import { getUnitTypeLabel } from '../constants/unitTypes'; // Importer depuis le nouveau fichier
import { FaLayerGroup, FaTable, FaBuilding, FaArrowRightFromBracket, FaPlus, FaTag, FaCalendarDays, FaUsers, FaArrowRight } from 'react-icons/fa6';
import logo from '../assets/logo.png';
import './Dashboard.css';
import Message from './Message';
import { purgeExpired } from '../services/expectedListStorage';

const Dashboard = () => {
    const [loading, setLoading] = useState(true);
    const [structure, setStructure] = useState(null);
    const [error, setError] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const navigate = useNavigate();

    // Charger les données de la structure et des unités
    const loadStructureData = useCallback(async () => {
        try {
            setLoading(true);
            const data = await UnitService.getStructureInfo();
            setStructure(data);
            setError('');
        } catch (err) {
            console.error('Erreur lors du chargement des données:', err);
            setError('Impossible de charger les données. Veuillez réessayer.');
            if (err.response?.status === 401) {
                // Token expiré ou invalide
                AuthService.logout();
                navigate('/signin');
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => { purgeExpired(); }, []);

    useEffect(() => {
        // Vérifier si l'utilisateur est authentifié
        if (!AuthService.isAuthenticated()) {
            navigate('/signin');
            return;
        }

        // Charger les données
        loadStructureData();
    }, [navigate, loadStructureData]);

    const handleLogout = () => {
        AuthService.logout();
        navigate('/signin');
    };

    if (loading && !structure) {
        return <div className="loading-container">Chargement...</div>;
    }

    const getUnitStatus = (unit) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Pour comparer uniquement les dates

        if (!unit.url) {
            return { text: 'Équipe non démarrée', className: 'status-red' };
        }
        if (new Date(unit.available_dt) > today) {
            return { text: 'En attente', className: 'status-orange' };
        }
        if (new Date(unit.end_dt) < today) {
            return { text: 'Terminé', className: 'status-gray' };
        }
        return { text: 'Actif', className: 'status-green' };
    };

    return (
        <div id="dashboard-container">
            <div id="dashboard-layout">
                <aside id="sidebar" className={sidebarOpen ? 'open' : ''}>
                    {/* Le contenu de la Sidebar sera stylisé via CSS */}
                    <div id="sidebar-header">
                        <h1>
                            <img src={logo} alt="Fiche Santé Logo" style={{ height: '30px', marginRight: '10px' }} /> Fiche Santé
                        </h1>
                    </div>
                    <nav id="sidebar-nav">
                        <ul>
                            <li>
                                <span className="active">
                                    <FaTable />
                                    <span>Tableau de bord</span>
                                </span>
                            </li>
                            <li>
                                <span onClick={() => navigate('/organization')}>
                                    <FaBuilding />
                                    <span>Mon organisation</span>
                                </span>
                            </li>
                        </ul>
                    </nav>
                </aside>

                {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}

                <main id="main-content">
                    <header id="header">
                        <button type="button" className="sidebar-toggle" aria-label="Menu" aria-expanded={sidebarOpen} onClick={() => setSidebarOpen(!sidebarOpen)}>
                            ☰
                        </button>
                        <div>
                            <h2>Tableau de bord</h2>
                        </div>
                        <div>
                            <button onClick={handleLogout} className="logout-button" aria-label="Déconnexion">
                                <FaArrowRightFromBracket />
                                <span>Déconnexion</span>
                            </button>
                        </div>
                    </header>

                    <section id="dashboard-body">
                        {error && <Message>{error}</Message>}

                        {structure && (
                            <>
                                <div id="welcome-banner">
                                    <div>
                                        <h3>Bienvenue {structure.name} !</h3>
                                        <p>Voici un aperçu de l'activité de votre organisation. Gérez facilement vos séjours et groupes depuis ce tableau de bord.</p>
                                    </div>
                                    <div>
                                        <FaLayerGroup />
                                    </div>
                                </div>

                                <div id="stays-section">
                                    <div id="stays-header">
                                        <h4>Groupes / Séjours</h4>
                                        <button onClick={() => navigate('/add-unit')}>
                                            <FaPlus />
                                            <span>Ajouter un groupe / un séjour</span>
                                        </button>
                                    </div>

                                    {structure.units && structure.units.length > 0 ? (
                                        <div id="stays-grid">
                                            {structure.units.map(unit => {
                                                const status = getUnitStatus(unit);
                                                return (
                                                    <div key={unit.id} className="stay-card" onClick={() => { navigate(`/unit/${unit.id}`) }}>
                                                        <div>
                                                            <h5>{unit.name}</h5>
                                                            <span className={`status-badge ${status.className}`}>{status.text}</span>
                                                        </div>
                                                        <div>
                                                            <p><FaTag /><span>Type : <strong>{getUnitTypeLabel(unit.type)}</strong></span></p>
                                                            <p><FaCalendarDays /><span>Du <strong>{new Date(unit.start_dt).toLocaleDateString('fr-FR')}</strong> au <strong>{new Date(unit.end_dt).toLocaleDateString('fr-FR')}</strong></span></p>
                                                            <p><FaUsers /><span>Participants : <strong>{parseInt(unit.profiles, 10) || 0}</strong></span></p>
                                                        </div>
                                                        <span>
                                                            Voir les détails <FaArrowRight />
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="no-units">Aucun groupe / séjour n'a été créé pour le moment.</p>
                                    )}
                                </div>
                            </>
                        )}
                    </section>
                </main>
            </div>
        </div>
    );
};

export default Dashboard;
